// Threshold (px) for "at bottom" before scroll passes through to document
const SCROLL_PASSTHROUGH_THRESHOLD = 10;

// Parent component that contains all the logic
class ProductMediaGrid extends HTMLElement {
  connectedCallback() {
    this.scrollContainer = this.querySelector('.product-media-grid__items');
    this.productGridItems = this.scrollContainer?.querySelectorAll('[data-product-grid-item]');
    this.rows = this.calculateRows();
    if (this.rows.length <= 1) return;
    this.createIndicators();
    this.setupObserver();
    this.setupScrollPassThrough();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.passThroughAbortController?.abort();
  }

  isScrollContainerAtBottom() {
    if (!this.scrollContainer) return false;
    const { scrollTop, clientHeight, scrollHeight } = this.scrollContainer;
    return scrollTop + clientHeight >= scrollHeight - SCROLL_PASSTHROUGH_THRESHOLD;
  }

  setupScrollPassThrough() {
    if (!this.scrollContainer || this.rows.length <= 1) return;

    this.passThroughAbortController = new AbortController();
    const { signal } = this.passThroughAbortController;

    this.scrollContainer.addEventListener(
      'wheel',
      (e) => {
        if (!this.isScrollContainerAtBottom() || e.deltaY <= 0) return;
        e.preventDefault();
        window.scrollBy({ top: e.deltaY, behavior: 'auto' });
      },
      { passive: false, signal }
    );

    let touchStartY = 0;
    this.scrollContainer.addEventListener(
      'touchstart',
      (e) => {
        touchStartY = e.touches[0].clientY;
      },
      { passive: true, signal }
    );

    this.scrollContainer.addEventListener(
      'touchmove',
      (e) => {
        if (!this.isScrollContainerAtBottom()) return;
        const touchY = e.touches[0].clientY;
        const deltaY = touchStartY - touchY;
        if (deltaY <= 0) return;
        e.preventDefault();
        window.scrollBy({ top: deltaY, behavior: 'auto' });
        touchStartY = touchY;
      },
      { passive: false, signal }
    );
  }

  calculateRows() {
    const rows = [];
    const items = [...this.productGridItems];
    let rowIndex = 0;
    let i = 0;

    while (i < items.length) {
      // Row 5 (index 4) has only 1 item, all others have 2 items
      const isRow5 = rowIndex === 4;
      const itemsInRow = isRow5 ? 1 : 2;
      const rowItems = items.slice(i, i + itemsInRow);

      if (rowItems.length > 0) {
        rows.push({
          index: rowIndex,
          items: rowItems
        });
      }

      i += itemsInRow;
      rowIndex++;
    }

    return rows;
  }

  createIndicators() {
    // Find or create the watcher component
    let watcher = this.querySelector('product-media-watcher');
    if (!watcher) {
      watcher = document.createElement('product-media-watcher');
      this.appendChild(watcher);
    }

    // Create container for dots
    const container = document.createElement('div');
    container.className = 'product-grid-indicators';

    // Create dots for each row
    this.rows.forEach((row) => {
      const dot = document.createElement('button');
      dot.setAttribute('type', 'button');
      dot.setAttribute('data-row-index', row.index);
      dot.setAttribute('aria-label', `Scroll to row ${row.index + 1}`);
      dot.className = 'product-grid-indicator';

      container.appendChild(dot);
    });

    watcher.appendChild(container);
    this.indicatorContainer = container;
    this.indicators = container.querySelectorAll('.product-grid-indicator');
  }

  setupObserver() {
    // Track which rows are currently intersecting
    this.intersectingRows = new Map();

    const options = {
      root: this.scrollContainer,
      rootMargin: '0px',
      threshold: [0, 0.25, 0.5, 0.75, 1.0]
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const item = entry.target;
        const rowIndex = this.findRowIndexForItem(item);

        if (rowIndex === -1) return;

        if (!entry.isIntersecting) {
          return this.intersectingRows.delete(rowIndex);
        }

        // Store the intersection data for this row
        this.intersectingRows.set(rowIndex, {
          ratio: entry.intersectionRatio,
          boundingClientRect: entry.boundingClientRect,
          rootBounds: entry.rootBounds
        });
      });

      // Determine which row should be active (the one with highest intersection ratio)
      if (this.intersectingRows.size <= 0) return;

      let maxRatio = 0;
      let activeRowIndex = 0;

      this.intersectingRows.forEach((data, rowIndex) => {
        if (data.ratio > maxRatio) {
          maxRatio = data.ratio;
          activeRowIndex = rowIndex;
        }
      });

      this.updateActiveIndicator(activeRowIndex);
    }, options);

    // Observe the first item of each row
    this.rows.forEach((row) => this.observer.observe(row.items[0]));
  }

  findRowIndexForItem(item) {
    for (let i = 0; i < this.rows.length; i++) {
      if (this.rows[i].items.includes(item)) return i;
    }
    return -1;
  }

  updateActiveIndicator(rowIndex) {
    // Remove active class from all indicators
    this.indicators.forEach((indicator) => {
      indicator.classList.remove('active');
    });

    // Add active class to current row indicator
    if (this.indicators[rowIndex]) {
      this.indicators[rowIndex].classList.add('active');
    }
  }

  scrollToRow(rowIndex) {
    const row = this.rows[rowIndex];
    if (!row || !row.items[0]) {
      return console.warn(`Row ${rowIndex} not found`);
    }

    const targetItem = row.items[0];
    const containerRect = this.scrollContainer.getBoundingClientRect();
    const targetRect = targetItem.getBoundingClientRect();

    // Calculate the scroll position needed (vertical scrolling)
    // We want to scroll so the target item is at the top of the container
    const scrollTop = this.scrollContainer.scrollTop + (targetRect.top - containerRect.top);

    // Scroll the container vertically
    this.scrollContainer.scrollTo({
      top: scrollTop,
      behavior: 'smooth'
    });
  }
}

// Child component that only listens to scroll and click events
class ProductMediaWatcher extends HTMLElement {
  connectedCallback() {
    this.grid = this.closest('product-media-grid');
    this.setupEventListeners();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  setupEventListeners() {
    if (!this.grid?.scrollContainer) return;

    // Create AbortController for managing event listeners
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    // Listen to click events on indicator buttons
    this.addEventListener("click", ({target}) => {
      const button = target.closest('.product-grid-indicator');
      if (!button) return;
      const rowIndex = parseInt(button.getAttribute('data-row-index'), 10);
      this.grid?.scrollToRow(rowIndex);
    }, { signal });

    // Listen to scroll events on the container (optional, for future enhancements)
    this.grid?.scrollContainer?.addEventListener('scroll', () => {}, { signal });
  }
}

window.ProductMediaGrid = ProductMediaGrid;
window.ProductMediaWatcher = ProductMediaWatcher;
customElements.define('product-media-grid', ProductMediaGrid);
customElements.define('product-media-watcher', ProductMediaWatcher);
